/**
 * Wall-clock time in a named zone, converted to an instant.
 *
 * Browser-safe on purpose: the goal builder converts a `datetime-local` value the moment a
 * person types it, and the orchestrator re-converts the same pair on the way in, because a
 * client is not a trust boundary. One implementation means the preview and the enforced
 * deadline cannot disagree.
 *
 * The rules that matter, and why they are not conveniences:
 *
 * - A **skipped** wall time (the hour daylight saving removes) does not exist. Silently
 *   shifting it to the next real instant moves a person's stop time by an hour in the
 *   direction they did not ask for, so strict mode refuses it instead.
 * - A **repeated** wall time (the hour daylight saving gives back) has two instants. The
 *   earlier one is used, because a supervisor that stops at the first 02:30 has stopped when
 *   the person said, and the later choice would quietly extend the run.
 *
 * This logic and its tests come from the retired assisted-run stop-time field, kept under a
 * neutral name: the arithmetic was never specific to that feature.
 */

const DAY_MS = 86_400_000;

export function isValidTimezone(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0 || value.length > 100) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

/** The zone the caller's environment is in, or UTC when it cannot be determined. */
export function currentTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

type LocalTimeProblem = 'invalid-format' | 'unknown-timezone' | 'nonexistent-time';

export type LocalTimeResult =
  | { ok: true; at: number; /** True when the wall time was ambiguous and the earlier instant was used. */ ambiguous: boolean }
  | { ok: false; problem: LocalTimeProblem; reason: string };

/**
 * Convert `YYYY-MM-DDTHH:mm` in `timezone` to an epoch-ms instant.
 *
 * The offset is found by formatting a guess back in the zone and measuring the difference —
 * how this has to be done without Temporal. Both sides of an offset change are probed so a
 * transition day is answered correctly rather than by a fixed offset assumption.
 */
export function zonedWallTimeToInstant(value: string, timezone: string): LocalTimeResult {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value.trim())) {
    return { ok: false, problem: 'invalid-format', reason: 'expected a date and time like 2026-09-19T21:00' };
  }
  if (!isValidTimezone(timezone)) {
    return { ok: false, problem: 'unknown-timezone', reason: `${timezone} is not a known IANA timezone` };
  }
  const [datePart = '', timePart = '00:00'] = value.trim().split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if (!year || !month || !day || !Number.isFinite(hour) || !Number.isFinite(minute)) {
    return { ok: false, problem: 'invalid-format', reason: 'expected a date and time like 2026-09-19T21:00' };
  }
  const guess = Date.UTC(year, month - 1, day, hour ?? 0, minute ?? 0);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const wallTime = (instant: number): number => {
    const parts = formatter.formatToParts(new Date(instant));
    const read = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? '0');
    return Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'));
  };
  const candidates = [-DAY_MS, 0, DAY_MS]
    .map((delta) => guess - (wallTime(guess + delta) - (guess + delta)))
    .filter((instant) => wallTime(instant) === guess);
  if (candidates.length === 0) {
    return {
      ok: false,
      problem: 'nonexistent-time',
      reason: `${value} does not exist in ${timezone} — daylight saving skips that hour`,
    };
  }
  const unique = [...new Set(candidates)];
  return { ok: true, at: Math.min(...unique), ambiguous: unique.length > 1 };
}

/**
 * The same conversion as an ISO string, throwing in strict mode.
 *
 * Kept for the report-window links, whose server side parses ISO. New callers should prefer
 * `zonedWallTimeToInstant`, which reports the DST problems instead of throwing.
 */
export function localInZoneToIso(value: string, timezone: string, strict = false): string {
  const result = zonedWallTimeToInstant(value, timezone);
  if (result.ok) return new Date(result.at).toISOString();
  if (strict) throw new Error(result.reason);
  const [datePart = '', timePart = '00:00'] = String(value).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if (!year || !month || !day) return new Date(value).toISOString();
  return new Date(Date.UTC(year, month - 1, day, hour ?? 0, minute ?? 0)).toISOString();
}

/** `2026-09-19T21:00` for an instant in a zone: the inverse, for prefilling a form. */
export function instantToZonedWallTime(at: number, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: isValidTimezone(timezone) ? timezone : 'UTC',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = formatter.formatToParts(new Date(at));
  const read = (type: string): string => parts.find((part) => part.type === type)?.value ?? '00';
  return `${read('year')}-${read('month')}-${read('day')}T${read('hour')}:${read('minute')}`;
}

/** "9:00 pm Australia/Melbourne" — what a person needs to check a deadline is the one they meant. */
export function describeInstantInZone(at: number, timezone: string): string {
  const zone = isValidTimezone(timezone) ? timezone : 'UTC';
  const formatted = new Intl.DateTimeFormat('en-AU', {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(at));
  // en-AU inserts a narrow no-break space before am/pm; normalise it so the string is
  // comparable and copyable.
  return `${formatted.replace(/[\u202F\u00A0]/g, ' ')} ${zone}`;
}
